import { ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { sendChatMessage } from '../../lib/forecasting_risk/chat-client';
import type { ChatMessage, ReportId } from '../../lib/forecasting_risk/chat-client';
import './AskSyntiro.css';

type Mode = 'bottom' | 'side';

const MODE_STORAGE_KEY = 'askSyntiro.mode';

const STARTERS: Record<ReportId, string[]> = {
  quantified_risk: [
    'Summarise the top three energy risks',
    "What is CCBA's carbon tax exposure?",
    'Explain the water‑energy nexus figure'
  ],
  forecasting: [
    'What is the full-year cost forecast vs budget?',
    'Which sites have critical alerts right now?',
    'What are the top recommended actions?'
  ]
};

const SCOPE_LINE = 'Grounded on this report only';

/** Light markdown: split on blank lines into paragraphs, **bold** inline, single newlines as breaks. */
function renderAnswer(text: string): ReactNode {
  return text.split(/\n{2,}/).map((block, bi) => (
    <p className="as-p" key={bi}>
      {block.split('\n').map((line, li) => (
        <span key={li}>
          {li > 0 && <br />}
          {line
            .split(/(\*\*[^*]+\*\*)/g)
            .map((part, pi) =>
              part.startsWith('**') && part.endsWith('**') ? (
                <strong key={pi}>{part.slice(2, -2)}</strong>
              ) : (
                <span key={pi}>{part}</span>
              )
            )}
        </span>
      ))}
    </p>
  ));
}

interface AskSyntiroProps {
  /** The report surface the assistant sits over (the iframe). */
  children: ReactNode;
  reportId: ReportId;
}

export default function AskSyntiro({ children, reportId }: AskSyntiroProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [mode, setMode] = useState<Mode>(() =>
    typeof window !== 'undefined' && window.localStorage.getItem(MODE_STORAGE_KEY) === 'side'
      ? 'side'
      : 'bottom'
  );
  const [sheetOpen, setSheetOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);

  const bottomThreadRef = useRef<HTMLDivElement>(null);
  const sideThreadRef = useRef<HTMLDivElement>(null);
  const sidePanelRef = useRef<HTMLDivElement>(null);
  const bottomInputRef = useRef<HTMLInputElement>(null);
  const sideInputRef = useRef<HTMLTextAreaElement>(null);
  const blurTimer = useRef<ReturnType<typeof setTimeout>>();

  // keep the visible thread pinned to the latest message
  useLayoutEffect(() => {
    for (const ref of [bottomThreadRef, sideThreadRef]) {
      if (ref.current) ref.current.scrollTop = ref.current.scrollHeight;
    }
  }, [messages, loading, mode, sheetOpen, collapsed]);

  // constrain the side panel to the remaining viewport height below its top edge
  useLayoutEffect(() => {
    const sidePanel = sidePanelRef.current;
    if (!sidePanel) return;
    const updateHeight = () => {
      sidePanel.style.height = `${window.innerHeight - sidePanel.getBoundingClientRect().top}px`;
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, [mode, collapsed]);

  const ask = useCallback(
    async (textArg?: string) => {
      const text = (textArg ?? input).trim();
      if (!text || loading) return;

      const next: ChatMessage[] = [...messages, { role: 'user', content: text }];
      setMessages(next);
      setInput('');
      setSuggestionsOpen(false);
      if (mode === 'bottom') setSheetOpen(true);
      setLoading(true);
      setError(null);

      try {
        const reply = await sendChatMessage(next, reportId);
        setMessages((prev) => [...prev, { role: 'model', content: reply }]);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      } finally {
        setLoading(false);
      }
    },
    [input, loading, messages, mode]
  );

  // The up arrow brings up the chat: send if there's text, otherwise just open the sheet.
  const arrowPress = useCallback(() => {
    if (input.trim()) {
      ask();
    } else {
      setSuggestionsOpen(false);
      setSheetOpen(true);
    }
  }, [input, ask]);

  const switchMode = useCallback(
    (m: Mode) => {
      setCollapsed(false);
      setMode(m);
      window.localStorage.setItem(MODE_STORAGE_KEY, m);
      if (m === 'bottom') {
        setSheetOpen(messages.length > 0);
      } else {
        setSuggestionsOpen(false);
        window.setTimeout(() => sideInputRef.current?.focus(), 60);
      }
    },
    [messages.length]
  );

  const newConversation = useCallback(() => {
    setMessages([]);
    setError(null);
  }, []);

  // bottom-mode suggestion bubbles: show on focus / when emptied, hide on type / blur
  const showSuggestions = useCallback(() => {
    if (mode === 'bottom' && !sheetOpen) setSuggestionsOpen(true);
  }, [mode, sheetOpen]);

  const onBottomChange = (value: string) => {
    setInput(value);
    if (mode === 'bottom' && !sheetOpen) setSuggestionsOpen(value.trim().length === 0);
  };

  const onBottomBlur = () => {
    // delay so a click on a suggestion bubble still registers
    blurTimer.current = setTimeout(() => setSuggestionsOpen(false), 150);
  };

  useEffect(() => () => clearTimeout(blurTimer.current), []);

  const growTextarea = (el: HTMLTextAreaElement) => {
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  };

  const thread = (
    <>
      {messages.length === 0 && !loading ? (
        <div className="as-empty">
          <h2>Ask anything about this report</h2>
          <p>{"I've read the full report — figures, assumptions and every risk category."}</p>
          <div className="as-starters">
            {STARTERS[reportId].map((s) => (
              <button type="button" key={s} className="as-starter" onClick={() => ask(s)}>
                {s}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <>
          {messages.map((m, i) =>
            m.role === 'user' ? (
              <div className="as-turn-user" key={i}>
                <div className="as-bub">{m.content}</div>
              </div>
            ) : (
              <div className="as-turn-ai" key={i}>
                <div className="as-who">
                  <span className="as-dot" />
                  Syntiro
                </div>
                <div className="as-ans">{renderAnswer(m.content)}</div>
              </div>
            )
          )}
          {loading && (
            <div className="as-turn-ai">
              <div className="as-who">
                <span className="as-dot" />
                Syntiro
              </div>
              <div className="as-typing">
                <i />
                <i />
                <i />
              </div>
            </div>
          )}
          {error && <div className="as-error">{error}</div>}
        </>
      )}
    </>
  );

  return (
    <div className={`as-main as-mode-${mode}${collapsed ? ' as-collapsed' : ''}`}>
      <div className="as-report">{children}</div>

      {/* SIDE MODE: docked panel */}
      <div className="as-side" ref={sidePanelRef}>
        <div className="as-head">
          <div className="as-spark">✦</div>
          <div>
            <div className="as-ttl">Ask Syntiro</div>
            <div className="as-scope">{SCOPE_LINE}</div>
          </div>
          <div className="as-acts">
            <button
              type="button"
              className="as-iconbtn"
              title="New conversation"
              onClick={newConversation}
            >
              ⟲
            </button>
            <button
              type="button"
              className="as-iconbtn"
              title="Dock to bottom"
              onClick={() => switchMode('bottom')}
            >
              ⤓<span className="as-t">Bottom</span>
            </button>
            <button
              type="button"
              className="as-iconbtn"
              title="Hide"
              onClick={() => setCollapsed(true)}
            >
              ✕
            </button>
          </div>
        </div>
        <div className="as-thread" ref={sideThreadRef}>
          {thread}
        </div>
        <div className="as-side-composer">
          <div className="as-side-inputwrap">
            <textarea
              ref={sideInputRef}
              rows={1}
              value={input}
              disabled={loading}
              placeholder="Ask about a figure, assumption, or risk…"
              onChange={(e) => {
                setInput(e.target.value);
                growTextarea(e.target);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  ask();
                }
              }}
            />
            <button type="button" className="as-send" onClick={() => ask()} disabled={loading}>
              ↑
            </button>
          </div>
          <div className="as-side-hint">Enter to send · Shift+Enter for newline</div>
        </div>
      </div>

      {/* SIDE MODE: collapsed rail */}
      <div
        className="as-side-rail"
        role="button"
        tabIndex={0}
        title="Show Ask Syntiro"
        onClick={() => setCollapsed(false)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') setCollapsed(false);
        }}
      >
        <div className="as-spark">✦</div>
        <div className="as-vlabel">ASK SYNTIRO</div>
      </div>

      {/* BOTTOM MODE: answer sheet */}
      <div className={`as-sheet${sheetOpen ? ' as-open' : ''}`}>
        <div className="as-head">
          <div className="as-spark">✦</div>
          <div>
            <div className="as-ttl">Ask Syntiro</div>
            <div className="as-scope">{SCOPE_LINE}</div>
          </div>
          <div className="as-acts">
            <button
              type="button"
              className="as-iconbtn"
              title="New conversation"
              onClick={newConversation}
            >
              ⟲
            </button>
            <button
              type="button"
              className="as-iconbtn"
              title="Dock to side"
              onClick={() => switchMode('side')}
            >
              ◧<span className="as-t">Side</span>
            </button>
            <button
              type="button"
              className="as-iconbtn"
              title="Close"
              onClick={() => setSheetOpen(false)}
            >
              ✕
            </button>
          </div>
        </div>
        <div className="as-thread" ref={bottomThreadRef}>
          {thread}
        </div>
      </div>

      {/* BOTTOM MODE: floating suggestion bubbles */}
      <div className={`as-suggestions${suggestionsOpen ? ' as-show' : ''}`}>
        <div className="as-sugg-hint">Try asking…</div>
        {STARTERS[reportId].map((s) => (
          <div
            key={s}
            className="as-sugg-bubble"
            role="button"
            tabIndex={-1}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => ask(s)}
          >
            {s}
          </div>
        ))}
      </div>

      {/* BOTTOM MODE: floating ask bar */}
      <div className="as-dock" style={{ bottom: sheetOpen ? 'calc(64% + 22px)' : '26px' }}>
        <div className="as-askbar">
          <div className="as-spark">✦</div>
          <input
            ref={bottomInputRef}
            value={input}
            disabled={loading}
            placeholder="Ask Syntiro about this report…"
            onChange={(e) => onBottomChange(e.target.value)}
            onFocus={showSuggestions}
            onBlur={onBottomBlur}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                arrowPress();
              }
            }}
          />
          <button
            type="button"
            className="as-mini"
            title="Dock to side"
            onClick={() => switchMode('side')}
          >
            ◧
          </button>
          <button
            type="button"
            className="as-send"
            title="Bring up the chat"
            onClick={arrowPress}
            disabled={loading}
          >
            ↑
          </button>
        </div>
      </div>
    </div>
  );
}
